import { useState, useEffect } from 'react'
import { Zap, BookOpen, Settings, Globe, LogOut, Plug, Folder, Edit3, Save, X, Camera } from 'lucide-react'
import api from '../services/api'

interface HeaderProps {
  onNavigate: (page: 'sessions' | 'chat' | 'skills' | 'knowledge' | 'stats' | 'hub' | 'captures' | 'connecteurs' | 'projects' | 'pcia' | 'pcfm' | 'pcarmor' | 'pctuf' | 'pcagents') => void
  currentPage: string
  onLogout?: () => void
}

export default function Header({ onNavigate, currentPage, onLogout }: HeaderProps) {
  const [showSoulModal, setShowSoulModal] = useState(false)
  const [soulContent, setSoulContent] = useState('')
  const [loadingSoul, setLoadingSoul] = useState(false)
  const [savingSoul, setSavingSoul] = useState(false)
  const [soulStatus, setSoulStatus] = useState<{ type: 'success' | 'error' | ''; message: string }>({ type: '', message: '' })

  const fetchSoul = async () => {
    setLoadingSoul(true)
    setSoulStatus({ type: '', message: '' })
    try {
      const response = await api.get('/api/projects/hermes/soul')
      setSoulContent(response.data.content || '')
    } catch (err: any) {
      console.error(err)
      setSoulStatus({
        type: 'error',
        message: 'Impossible de charger le prompt système de Hermes.'
      })
    } finally {
      setLoadingSoul(false)
    }
  }

  const saveSoul = async () => {
    setSavingSoul(true)
    setSoulStatus({ type: '', message: '' })
    try {
      await api.put('/api/projects/hermes/soul', { content: soulContent })
      setSoulStatus({
        type: 'success',
        message: 'Prompt système enregistré avec succès !'
      })
      setTimeout(() => setSoulStatus({ type: '', message: '' }), 4000)
    } catch (err: any) {
      console.error(err)
      setSoulStatus({
        type: 'error',
        message: 'Erreur lors de la sauvegarde du prompt.'
      })
    } finally {
      setSavingSoul(false)
    }
  }

  useEffect(() => {
    if (showSoulModal) {
      fetchSoul()
    }
  }, [showSoulModal])

  const handleLogout = () => {
    if (onLogout) {
      onLogout()
    }
  }

  return (
    <header style={{
      backgroundColor: '#111827',
      borderBottom: '1px solid #374151',
      padding: '16px 24px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Logo */}
        <h1 style={{ color: '#ec4899', fontSize: '20px', fontWeight: 'bold', margin: '0' }}>
          ⚡ CLAUDIE V3
        </h1>

        {/* Navigation + Logout */}
        <nav style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', rowGap: '8px' }}>
          {/* Cockpit Kermhit — tableau de bord perso (Kermhit 02/11/2026), Tailscale, nouvel onglet */}
          <a
            href="http://100.127.183.4:8098/"
            target="_blank"
            rel="noopener noreferrer"
            title="Tableau de bord Kermhit (Tailscale)"
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '600',
              transition: 'all 0.2s',
              backgroundColor: '#2a2213',
              color: '#d4a24c',
              border: '1px solid #d4a24c',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            🎛️ Cockpit Kermhit
          </a>

          {/* Sessions - page d'accueil (enregistrements CLI asciinema) */}
          <button
            onClick={() => onNavigate('sessions')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === 'sessions' ? '#ec4899' : 'transparent',
              color: currentPage === 'sessions' ? '#ffffff' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== 'sessions') e.currentTarget.style.backgroundColor = '#374151'
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 'sessions') e.currentTarget.style.backgroundColor = 'transparent'
            }}
            title="Sessions — conversations CLI (Claude Code) en lecture"
          >
            💬 Sessions
          </button>



          {/* Skills */}
          <button
            onClick={() => onNavigate('skills')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === 'skills' ? '#ec4899' : 'transparent',
              color: currentPage === 'skills' ? '#ffffff' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== 'skills') {
                e.currentTarget.style.backgroundColor = '#374151'
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 'skills') {
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <Zap size={18} />
            Skills
          </button>

          {/* Knowledge */}
          <button
            onClick={() => onNavigate('knowledge')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === 'knowledge' ? '#ec4899' : 'transparent',
              color: currentPage === 'knowledge' ? '#ffffff' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== 'knowledge') {
                e.currentTarget.style.backgroundColor = '#374151'
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 'knowledge') {
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <BookOpen size={18} />
            Knowledge
          </button>

          {/* Stats */}
          <button
            onClick={() => onNavigate('stats')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === 'stats' ? '#ec4899' : 'transparent',
              color: currentPage === 'stats' ? '#ffffff' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== 'stats') {
                e.currentTarget.style.backgroundColor = '#374151'
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 'stats') {
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <Settings size={18} />
            Cockpit
          </button>

          {/* Hub */}
          <button
            onClick={() => onNavigate('hub')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === 'hub' ? '#ec4899' : 'transparent',
              color: currentPage === 'hub' ? '#ffffff' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== 'hub') {
                e.currentTarget.style.backgroundColor = '#374151'
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 'hub') {
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <Globe size={18} />
            Hub
          </button>

          {/* Captures */}
          <button
            onClick={() => onNavigate('captures')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === 'captures' ? '#ec4899' : 'transparent',
              color: currentPage === 'captures' ? '#ffffff' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== 'captures') {
                e.currentTarget.style.backgroundColor = '#374151'
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 'captures') {
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <Camera size={18} />
            Captures
          </button>

          {/* Connecteurs */}
          <button
            onClick={() => onNavigate('connecteurs')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === 'connecteurs' ? '#ec4899' : 'transparent',
              color: currentPage === 'connecteurs' ? '#ffffff' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== 'connecteurs') e.currentTarget.style.backgroundColor = '#374151'
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 'connecteurs') e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <Plug size={18} />
            Connecteurs
          </button>

          {/* Projets */}
          <button
            onClick={() => onNavigate('projects')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === 'projects' || currentPage === 'project-detail' ? '#ec4899' : 'transparent',
              color: currentPage === 'projects' || currentPage === 'project-detail' ? '#ffffff' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== 'projects' && currentPage !== 'project-detail') {
                e.currentTarget.style.backgroundColor = '#374151'
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 'projects' && currentPage !== 'project-detail') {
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <Folder size={18} />
            Projets
          </button>

          {/* PC IA */}
          <button
            onClick={() => onNavigate('pcia')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: currentPage === 'pcia' ? '#ec4899' : 'transparent',
              color: currentPage === 'pcia' ? '#ffffff' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (currentPage !== 'pcia') {
                e.currentTarget.style.backgroundColor = '#374151'
              }
            }}
            onMouseLeave={(e) => {
              if (currentPage !== 'pcia') {
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
            title="PC MEGAPORT — tous les outils de la machine kermhitaa (IA locale)"
          >
            🏭 PC MEGAPORT
          </button>

          {/* Portable FM */}
          <button
            onClick={() => onNavigate('pcfm')}
            style={{ padding: '8px 16px', borderRadius: '6px', fontWeight: '500', transition: 'all 0.2s',
              backgroundColor: currentPage === 'pcfm' ? '#ec4899' : 'transparent',
              color: currentPage === 'pcfm' ? '#ffffff' : '#d1d5db',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            onMouseEnter={(e) => { if (currentPage !== 'pcfm') { e.currentTarget.style.backgroundColor = '#374151' } }}
            onMouseLeave={(e) => { if (currentPage !== 'pcfm') { e.currentTarget.style.backgroundColor = 'transparent' } }}
            title="Portable FM — outils du portable France Metal (surf / clip / veille)"
          >
            💻 Portable FM
          </button>

          {/* PC-D-agents */}
          <button
            onClick={() => onNavigate('pcagents')}
            style={{ padding: '8px 16px', borderRadius: '6px', fontWeight: '500', transition: 'all 0.2s',
              backgroundColor: currentPage === 'pcagents' ? '#ec4899' : 'transparent',
              color: currentPage === 'pcagents' ? '#ffffff' : '#d1d5db',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            onMouseEnter={(e) => { if (currentPage !== 'pcagents') { e.currentTarget.style.backgroundColor = '#374151' } }}
            onMouseLeave={(e) => { if (currentPage !== 'pcagents') { e.currentTarget.style.backgroundColor = 'transparent' } }}
            title="PC-D-agents — outils du PC des agents (captures, Hermes, Grist)"
          >
            🤖 PC agents
          </button>

          {/* ARMOR */}
          <button
            onClick={() => onNavigate('pcarmor')}
            style={{ padding: '8px 16px', borderRadius: '6px', fontWeight: '500', transition: 'all 0.2s',
              backgroundColor: currentPage === 'pcarmor' ? '#ec4899' : 'transparent',
              color: currentPage === 'pcarmor' ? '#ffffff' : '#d1d5db',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            onMouseEnter={(e) => { if (currentPage !== 'pcarmor') { e.currentTarget.style.backgroundColor = '#374151' } }}
            onMouseLeave={(e) => { if (currentPage !== 'pcarmor') { e.currentTarget.style.backgroundColor = 'transparent' } }}
            title="ARMOR — PC fixe de la maison (outils + inventaire)"
          >
            🛡️ ARMOR
          </button>

          {/* TUF */}
          <button
            onClick={() => onNavigate('pctuf')}
            style={{ padding: '8px 16px', borderRadius: '6px', fontWeight: '500', transition: 'all 0.2s',
              backgroundColor: currentPage === 'pctuf' ? '#ec4899' : 'transparent',
              color: currentPage === 'pctuf' ? '#ffffff' : '#d1d5db',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            onMouseEnter={(e) => { if (currentPage !== 'pctuf') { e.currentTarget.style.backgroundColor = '#374151' } }}
            onMouseLeave={(e) => { if (currentPage !== 'pctuf') { e.currentTarget.style.backgroundColor = 'transparent' } }}
            title="TUF — portable nomade (outils + inventaire)"
          >
            🎒 TUF
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              fontWeight: '500',
              transition: 'all 0.2s',
              backgroundColor: 'transparent',
              color: '#ef4444',
              border: '1px solid #ef4444',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginLeft: '12px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#7f1d1d'
              e.currentTarget.style.borderColor = '#dc2626'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.borderColor = '#ef4444'
            }}
            title="Déconnexion"
          >
            <LogOut size={16} />
          </button>
        </nav>
      </div>

      {showSoulModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            border: '1px solid #4b5563',
            width: '100%',
            maxWidth: '900px',
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 20px rgba(244, 63, 94, 0.15)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #374151',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#111827'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Edit3 size={20} style={{ color: '#f43f5e' }} />
                <h2 style={{ color: '#f3f4f6', fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                  Configuration du Prompt Système de Hermes (SOUL.md)
                </h2>
              </div>
              <button
                onClick={() => setShowSoulModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>
              <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
                Ce prompt système définit l'identité, le comportement, et les règles d'auto-amélioration de Hermes. Modifiez-le ci-dessous et enregistrez pour appliquer les changements immédiatement.
              </p>

              {soulStatus.type && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  backgroundColor: soulStatus.type === 'success' ? '#065f46' : '#7f1d1d',
                  color: soulStatus.type === 'success' ? '#34d399' : '#fca5a5',
                  border: `1px solid ${soulStatus.type === 'success' ? '#059669' : '#dc2626'}`
                }}>
                  {soulStatus.message}
                </div>
              )}

              {loadingSoul ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f43f5e' }}>
                  Chargement du Prompt Système...
                </div>
              ) : (
                <textarea
                  value={soulContent}
                  onChange={(e) => setSoulContent(e.target.value)}
                  placeholder="# Instructions Système..."
                  style={{
                    flex: 1,
                    backgroundColor: '#111827',
                    color: '#e5e7eb',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #4b5563',
                    resize: 'none',
                    lineHeight: '1.6',
                    outline: 'none',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)'
                  }}
                />
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #374151',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '12px',
              backgroundColor: '#111827'
            }}>
              <button
                onClick={() => setShowSoulModal(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  color: '#d1d5db',
                  border: '1px solid #4b5563',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Annuler
              </button>
              <button
                onClick={saveSoul}
                disabled={savingSoul || loadingSoul}
                style={{
                  padding: '8px 20px',
                  borderRadius: '6px',
                  backgroundColor: '#f43f5e',
                  color: '#ffffff',
                  border: 'none',
                  cursor: (savingSoul || loadingSoul) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (savingSoul || loadingSoul) ? 0.6 : 1,
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 6px -1px rgba(244, 63, 94, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (!savingSoul && !loadingSoul) e.currentTarget.style.backgroundColor = '#e11d48'
                }}
                onMouseLeave={(e) => {
                  if (!savingSoul && !loadingSoul) e.currentTarget.style.backgroundColor = '#f43f5e'
                }}
              >
                <Save size={16} />
                {savingSoul ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
